import {useMemo, useState} from "react";
import {GridColDef, GridRowParams, GridRowSelectionModel, GridSortModel} from "@mui/x-data-grid-pro";
import {DataGrid} from "@dain/mui-components";
import {useTranslation} from "react-i18next";
import {EmailDeliveryRecipientType, SubmissionDeliveryChannel, SubmissionDeliveryDto} from "../../../client/submission/SubmissionApiClient";
import { TranslationFunction } from "../../../i18n/i18n";
import {DateTimeFormatter} from "../../../common/DateTimeFormatter";
import {SubmissionTranslKey} from "../SubmissionTranslKey";
import {SubmissionDeliveryStateTypeFormatter} from "./SubmissionDeliveryStateTypeFormatter";
import {SubmissionDeliveryChannelFormatter} from "./SubmissionDeliveryChannelFormatter";

function formatRecipientType(row: SubmissionDeliveryDto, t: TranslationFunction): string {
    if (row.channel === SubmissionDeliveryChannel.ISDS) {
        return t(SubmissionTranslKey.DELIVERY_RECIPIENT_TYPE_RECIPIENT);
    }
    if (row.recipientType === EmailDeliveryRecipientType.RECIPIENT) {
        return t(SubmissionTranslKey.DELIVERY_RECIPIENT_TYPE_RECIPIENT);
    }
    if (row.recipientType === EmailDeliveryRecipientType.SENDER) {
        return t(SubmissionTranslKey.DELIVERY_RECIPIENT_TYPE_SENDER);
    }
    return "";
}

function buildSubmissionDeliveryGridColumns(t: TranslationFunction): GridColDef<SubmissionDeliveryDto>[] {
    const submissionGridColumns: GridColDef<SubmissionDeliveryDto>[] = [
        {
            field: "channel",
            headerName: t(SubmissionTranslKey.DELIVERY_CHANNEL),
            width: 150,
            valueFormatter: params => SubmissionDeliveryChannelFormatter.formatSubmissionDeliveryChannel(params.value, t),
        },
        {
            field: "recipient",
            headerName: t(SubmissionTranslKey.DELIVERY_RECIPIENT),
            width: 220,
            valueGetter: params => params.row.isdsRecipientDatabox ?? params.row.emailRecipient,
        },
        {
            field: "recipientType",
            headerName: t(SubmissionTranslKey.DELIVERY_RECIPIENT_TYPE),
            width: 180,
            valueGetter: params => formatRecipientType(params.row, t),
        },
        {
            field: "currentStateType",
            headerName: t(SubmissionTranslKey.DELIVERY_CURRENT_STATE_TYPE),
            width: 210,
            valueFormatter: params => SubmissionDeliveryStateTypeFormatter.formatSubmissionDeliveryStateType(params.value, t)
        },
        {
            field: "updatedAt",
            type: "dateTime",
            headerName: t(SubmissionTranslKey.DELIVERY_UPDATED_AT),
            width: 170,
            valueGetter: params => params.value ? new Date(params.value) : undefined,
            valueFormatter: params => DateTimeFormatter.formatDateTime(params.value)
        },
    ];

    return submissionGridColumns;
}

export interface SubmissionDeliveryGridProps {
    submissionDelivery: SubmissionDeliveryDto[];
    onRowSelected?: (delivery: SubmissionDeliveryDto | undefined) => void;
}

export default function SubmissionDeliveryGrid(props: Readonly<SubmissionDeliveryGridProps>) {
    const {submissionDelivery, onRowSelected} = props;

    const {t} = useTranslation();

    const defaultSortModel: GridSortModel = useMemo(() => [
        {field: "updatedAt", sort: "desc"},
    ], []);

    const [sortModel, setSortModel] = useState(defaultSortModel);
    const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);

    const columnDefs: GridColDef<SubmissionDeliveryDto>[] = useMemo(() =>
        buildSubmissionDeliveryGridColumns(t), [t]);

    function handleSelectionChange(model: GridRowSelectionModel) {
        setSelectionModel(model);
        const selectedId = model[0];
        const selected = selectedId !== undefined
            ? submissionDelivery.find(d => d.id === Number(selectedId))
            : undefined;
        onRowSelected?.(selected);
    }

    function handleRowClick(params: GridRowParams) {
        if (selectionModel[0] === params.id) {
            handleSelectionChange([]);
        }
    }

    return (
        <DataGrid
            rows={submissionDelivery}
            columns={columnDefs}
            autoHeight
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={handleSelectionChange}
            onRowClick={handleRowClick}
        />
    );
}
